import type { ShiptimeMegaShipmentProcessCreate } from '@shipium-corp/shipium-ts-types';
import type { HazmatCriteriaOptions } from '~/pages/PartnerConfigPages/Rulesets/types';
import { HazmatCriteriaOptions as HazmatCriteria } from '~/pages/PartnerConfigPages/Rulesets/types';

export type DestinationType = 'standard' | 'po_box' | 'hc' | 'rr';

// Subset of the FC's origin that shiptime's FCD lookup keys off.
export interface RuleTestShipFromAddress {
  countryCode: string;
  postalCode: string;
  street1?: string;
  city?: string;
  state?: string;
}

export interface RuleTestFormValues {
  fulfillmentContextId: string;
  tenantId?: string;
  inactiveRuleIdsToApply?: string[];
  destinationCountry?: string;
  destinationRegion?: string;
  destinationPostalCode?: string;
  destinationType?: DestinationType;
  weightValue?: number;
  weightUnit?: 'g' | 'kg' | 'oz' | 'lb';
  shipmentTags?: string[];
  shipOption?: string;
  exactDateDelivery?: boolean;
  desiredDeliveryDate?: Date;
  hazardousCriteriaOptions?: HazmatCriteriaOptions;
  carrierServiceMethodAllowList?: string[];
}

const HAZMAT_TAG_FOR: Partial<Record<HazmatCriteriaOptions, string>> = {
  [HazmatCriteria.LIMITED_QUANTITY]: 'limited-quantity',
  [HazmatCriteria.HAZMAT]: 'hazmat',
  [HazmatCriteria.FULL_HAZMAT]: 'full-hazmat',
};

const DEFAULT_DESTINATION = {
  name: 'Jane Doe',
  street1: '59 E 73rd St',
  city: 'New York',
  state: 'NY',
  postalCode: '10021',
  countryCode: 'US',
  addressType: 'residential' as const,
  phoneNumber: '2813308004',
};

const DEFAULT_TOTAL_WEIGHT = { weight: 1, weightUnit: 'lb' as const };

// Default form values pre-filled into the test form so the user sees exactly
// what would otherwise be sent on unfilled fields. Kept in sync with the
// per-field fallbacks below so prefilling doesn't change the resulting request.
export const RULE_TEST_FORM_DEFAULTS: Partial<RuleTestFormValues> = {
  destinationCountry: DEFAULT_DESTINATION.countryCode,
  destinationRegion: DEFAULT_DESTINATION.state,
  destinationPostalCode: DEFAULT_DESTINATION.postalCode,
  destinationType: 'standard',
  weightValue: DEFAULT_TOTAL_WEIGHT.weight,
  weightUnit: DEFAULT_TOTAL_WEIGHT.weightUnit,
  hazardousCriteriaOptions: HazmatCriteria.NONE,
};

const DEFAULT_PACKAGING_TYPE = {
  packagingMaterial: 'box' as const,
  packagingSizeName: 'rule-test-package',
  linearDimensions: { height: 1, length: 6, width: 4, linearUnit: 'in' as const },
  packagingWeight: { weight: 0.5, weightUnit: 'lb' as const },
};

const DEFAULT_ORDER_ITEMS = [{ productId: 'ET-14-4000', quantity: 2 }];

const buildShipmentTags = (values: RuleTestFormValues): string[] => {
  const tags = [...(values.shipmentTags ?? [])];
  const hazmatTag = values.hazardousCriteriaOptions ? HAZMAT_TAG_FOR[values.hazardousCriteriaOptions] : undefined;
  if (hazmatTag && !tags.includes(hazmatTag)) {
    tags.push(hazmatTag);
  }
  return tags;
};

const STREET1_FOR_DESTINATION_TYPE: Partial<Record<DestinationType, string>> = {
  po_box: 'PO Box 1',
  hc: 'HC 1 Box 1',
  rr: 'RR 1 Box 1',
};

export const buildRuleTestRequest = (
  values: RuleTestFormValues,
  shipFromAddress: RuleTestShipFromAddress,
): ShiptimeMegaShipmentProcessCreate => {
  const now = new Date().toISOString();
  const shipmentTags = buildShipmentTags(values);
  const street1Override = values.destinationType ? STREET1_FOR_DESTINATION_TYPE[values.destinationType] : undefined;

  const request = {
    generateLabel: false,
    currencyCode: 'usd',
    ...(values.carrierServiceMethodAllowList?.length
      ? { carrierServiceMethodAllowList: values.carrierServiceMethodAllowList }
      : {}),
    ...(values.inactiveRuleIdsToApply?.length ? { inactiveRuleIdsToApply: values.inactiveRuleIdsToApply } : {}),
    shipmentParameters: {
      testMode: true,
      orderedDateTime: now,
      shippedDateTime: now,
      orderItemQuantities: DEFAULT_ORDER_ITEMS,
      shipFromAddress,
      destinationAddress: {
        ...DEFAULT_DESTINATION,
        ...(street1Override ? { street1: street1Override } : {}),
        ...(values.destinationCountry ? { countryCode: values.destinationCountry } : {}),
        ...(values.destinationRegion ? { state: values.destinationRegion } : {}),
        ...(values.destinationPostalCode ? { postalCode: values.destinationPostalCode } : {}),
      },
      totalWeight:
        values.weightValue !== undefined && values.weightValue !== null
          ? { weight: values.weightValue, weightUnit: values.weightUnit ?? 'lb' }
          : DEFAULT_TOTAL_WEIGHT,
      packagingType: DEFAULT_PACKAGING_TYPE,
      ...(shipmentTags.length ? { shipmentTags } : {}),
      ...(values.tenantId ? { tenantId: values.tenantId } : {}),
      ...(values.shipOption ? { shipOption: values.shipOption } : {}),
      ...(values.exactDateDelivery
        ? {
            desiredDeliveryDateOptions: { exactDateDelivery: true },
            ...(values.desiredDeliveryDate
              ? { desiredDeliveryDate: values.desiredDeliveryDate.toISOString() }
              : { businessDaysOfTransit: 4 }),
          }
        : {}),
      fulfillmentContextIds: [values.fulfillmentContextId],
    },
  };
  return request as ShiptimeMegaShipmentProcessCreate;
};
